import type { SeatView } from '@rummy/protocol';
import { useEffect, useState } from 'react';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import { Portrait } from './Portrait.tsx';
import { publicMoveLabel, type PublicSeatMove } from './TableIntel.tsx';

const EMOTES: Record<string, string> = {
  clap: '👏',
  think: '🤔',
  quick: '⚡',
  nice: '✨',
  run: '🪄',
  close: '😮',
  wild: '🃏',
  oops: '😅',
  wave: '👋',
};

/**
 * How often the ring steps. Four times a second moves the arc about a pixel per
 * step on a 30-second turn, which reads as continuous. Stepping on every
 * animation frame instead kept the table re-rendering at the display's refresh
 * rate for the whole hand, for movement nobody could see.
 */
const RING_STEP_MS = 250;

/**
 * §7 — timing tells. Opponents never see exact per-action latency, only this
 * coarse ring. It is drawn as a smooth arc rather than a countdown to the
 * tenth of a second, which is what would let a careful observer read
 * hesitation.
 */
function TurnRing({ endsAt, total }: { endsAt: number; total: number }) {
  const [fraction, setFraction] = useState(1);

  useEffect(() => {
    const tick = () => {
      const remaining = Math.max(0, endsAt - Date.now());
      setFraction(Math.max(0, Math.min(1, remaining / (total * 1000))));
    };
    tick();
    const id = setInterval(tick, RING_STEP_MS);
    return () => clearInterval(id);
  }, [endsAt, total]);

  const r = 21;
  const circumference = 2 * Math.PI * r;
  return (
    <svg className="turn-ring" viewBox="0 0 48 48" aria-hidden="true">
      <circle className="turn-ring__track" cx="24" cy="24" r={r} />
      <circle
        className={`turn-ring__arc${fraction < 0.25 ? ' is-urgent' : ''}`}
        cx="24"
        cy="24"
        r={r}
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - fraction)}
      />
    </svg>
  );
}

export interface SeatCardProps {
  seat: SeatView;
  isTurn: boolean;
  isYou: boolean;
  turnEndsAt: number | null;
  turnTimer: number;
  showScore: boolean;
  /** Still owes an arrangement in the post-declaration window (§3.5). */
  arranging?: boolean;
  /** Reports the seat's element so a drawn card can fly to it. */
  onMount?: (element: HTMLElement | null) => void;
  /** Last public action only — a compact memory cue, never a timing tell. */
  publicMove?: PublicSeatMove;
  /** Position in the opponent row, so the row can curve into an arc. */
  index?: number;
  count?: number;
  /** Direction from the centre of the felt, for the wide layout that seats
   *  opponents round the table. Your own seat has none. */
  orbit?: { x: number; y: number };
  /**
   * Turns until this seat plays: 0 is the seat on move, 1 is next. `null` for a
   * seat that is not in the running order at all — dropped, out, or between
   * hands.
   */
  turnsAway?: number | null;
}

/**
 * How far this seat sits below the top of the arc.
 *
 * The row is a chord of an ellipse: the middle seats sit highest, the ends
 * lowest, so five opponents read as an arc curving toward the player rather
 * than as a line of cards. A single seat, and the centre of an odd row, get
 * zero — the quadratic is in *normalised* position, so the shape does not
 * change with the count, only its sampling does.
 */
function seatLift(index: number, count: number): number {
  if (count < 2) return 0;
  const u = (2 * (index + 0.5)) / count - 1;
  return Math.round(u * u * 26);
}

/**
 * The seat's emote while it is showing. It schedules its own re-render for the
 * moment it expires instead of relying on the table to happen to re-render.
 */
function useLiveEmote(emote: SeatView['emote']): string | null {
  const until = emote?.until ?? 0;
  const [, setExpired] = useState(0);
  useEffect(() => {
    const left = until - Date.now();
    if (left <= 0) return;
    const id = setTimeout(() => setExpired(until), left + 5);
    return () => clearTimeout(id);
  }, [until]);
  return emote && emote.until > Date.now() ? emote.key : null;
}

/**
 * Opponents occupy an arc of seat cards (§6.2): avatar, handle, card count,
 * turn indicator, a slim timer ring. Not a felt oval, not a scattered baize.
 */
export function SeatCard({
  seat,
  isTurn,
  isYou,
  turnEndsAt,
  turnTimer,
  showScore,
  arranging = false,
  onMount,
  publicMove = null,
  index = 0,
  count = 1,
  orbit,
  turnsAway = null,
}: SeatCardProps) {
  const { t } = useI18n();
  const emote = useLiveEmote(seat.emote);
  const dropped = seat.status === 'dropped' || seat.status === 'auto-dropped';
  const isNext = turnsAway === 1;

  return (
    <div
      ref={onMount}
      style={
        {
          '--seat-lift': `${seatLift(index, count)}px`,
          ...(orbit ? { '--orbit-x': orbit.x.toFixed(3), '--orbit-y': orbit.y.toFixed(3) } : {}),
        } as React.CSSProperties
      }
      className={[
        'seat',
        isTurn ? 'is-turn' : '',
        isYou ? 'is-you' : '',
        seat.eliminated ? 'is-out' : '',
        seat.connection !== 'connected' ? 'is-away' : '',
        dropped ? 'is-dropped' : '',
        isNext ? 'is-next' : '',
        arranging ? 'is-arranging' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="seat__mark">
        <Portrait
          character={seat.character ?? seat.avatar}
          handle={seat.handle}
          size={44}
          dim={seat.eliminated}
        />
        {isTurn && turnEndsAt !== null && <TurnRing endsAt={turnEndsAt} total={turnTimer} />}
        {emote && <span className="seat__emote">{EMOTES[emote] ?? '·'}</span>}
      </div>

      <div className="seat__body">
        <div className="seat__name">
          <span className="seat__handle">{seat.handle}</span>
          {seat.isHost && <span className="tag">{t('room.host')}</span>}
          {seat.isBot && <span className="tag tag--quiet">{t('room.bot')}</span>}
          {isYou && <span className="tag tag--quiet">{t('room.you')}</span>}
        </div>

        {/*
          Where this seat sits in the running order, counted from whoever is on
          move. The arc is already drawn in play order, but a row of cards says
          nothing about direction or distance — and on your own turn the glow is
          on *your* card, which is the one place you are not looking. "Next" and
          "+2" answer both questions without a second glance.
        */}
        {turnsAway !== null && turnsAway > 0 && (
          <span className={`seat__order${isNext ? ' seat__order--next' : ''}`}>
            {isNext ? t('table.order.next') : t('table.order.away', { count: turnsAway })}
          </span>
        )}

        <div className="seat__meta">
          {/* A drop is settled for the rest of the hand, so it outranks the
              transient states below it: a seat that has folded is not
              "reconnecting", and a card count for a hand nobody is playing is
              worse than no line at all. */}
          {dropped ? (
            <span className="seat__status seat__status--dropped">
              {t(seat.status === 'auto-dropped' ? 'table.autoDropped' : 'table.dropped')}
            </span>
          ) : seat.connection === 'reconnecting' ? (
            <span className="seat__status seat__status--away">{t('table.reconnecting')}</span>
          ) : arranging ? (
            <span className="seat__status seat__status--busy">{t('show.arranging')}</span>
          ) : seat.eliminated ? (
            <span className="seat__status">{t('table.eliminated')}</span>
          ) : (
            <span className="seat__count" aria-live="off">
              {/* A count, never identities — that is the projection's whole job.
                  Written out: a bar of tick marks had to be counted to be read. */}
              {t('seat.cards', { count: seat.cardCount })}
            </span>
          )}
          {showScore && <span className="seat__score">{t('points.short', { points: seat.score })}</span>}
          {publicMove && <span className="seat__public-move">{publicMoveLabel(publicMove)}</span>}
        </div>

        {seat.isBot && seat.character && (
          <span className="seat__style">{t(`bot.style.${seat.character}` as MessageKey)}</span>
        )}

        {seat.consecutiveAutoPlays >= 2 && !dropped && (
          <p className="seat__warning">
            {t('table.autoplay.warning', {
              name: seat.handle,
              count: seat.consecutiveAutoPlays,
            })}
          </p>
        )}
      </div>
    </div>
  );
}

export { EMOTES };
